import { createMemo, createSignal, onMount } from "solid-js";
import { createStore } from "solid-js/store";

import {
  CheckEnabledRegisterValidateCode,
  CheckUserAccExists,
  GetPhoneVerifyCode,
  VerifyRegisterCode,
  useRegister as useRegisterApi,
} from "@/api/clientApi/login.ts";
import { message } from "@/components/Base/Message/Message.tsx";
import DialogSettingCenter from "@/components/Dialogs/DialogCenter.ts";
import { DialogType } from "@/components/Dialogs/types";
import { useDialog } from "@/components/Dialogs/useDialog";
import {
  errorMessagesBaseInfo,
  errorMessagesPersonProfile,
  validators,
} from "@/components/PC/Register/Validators";
import type {
  Country,
  Option,
  RegisterBaseInfo,
  RegisterRegFormConfig,
} from "@/components/PC/Register/type";
import { LocalStorageKey, PHONE_CODE } from "@/constants/constants.ts";
import {
  CodeTypeEnum,
  LoginTypeEnum,
  VerifyTypeEnum,
} from "@/enum/loginRegister.ts";
import { useAfterLogin } from "@/hooks/login/useAfterLogin";
import { getLangKey, useI18n } from "@/i18n";
import { aesEncryptWithCommonKey, rsaEncrypt } from "@/lib/aesUtils.ts";
import { useTimestampInSeconds } from "@/lib/hooks/useTimestamp.ts";
import { isMobile } from "@/lib/isMobile.ts";
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from "@/lib/localStorage.ts";
import { sdkManager } from "@/lib/sdk/sdkManager.ts";
import { isTwaConnected } from "@/lib/twaBridge.ts";
import {
  addZero,
  convertObjectToStringRecord,
  getAppType,
  getCurrentVersion,
  getOS,
} from "@/lib/utils.ts";
import { configStore, getWebsiteConfigV2 } from "@/stores/configStore.ts";
import { deviceInfo } from "@/stores/deviceStore.ts";
import {
  getAdParams,
  getAdReferer,
  getAppPackageInfoParams,
  getSdkData,
  getTwaData,
  sysConfigStore,
  tryGetServerHash,
} from "@/stores/sysConfigStore.ts";
import { getUserInfo, upDateUserStore } from "@/stores/userStore";
import {
  CheckUserAccExistsReq,
  GetPhoneVerifyCodeReq,
  RegisterV3Req,
  RegisterV3ReqBaseInfo,
  RegisterV3ReqPersonProfile,
  RegisterV3ReqVadidateCode,
} from "@/types/swagger/loginV2";

import { getHomePopups } from "../usePopup.ts";
import { useRouter } from "../useRouter.tsx";
import { useUrlStorage } from "../useUrlStorage.ts";

// 默认表单数据
const defaultBaseInfo: RegisterBaseInfo = {
  phone: "",
  email: "",
  a_code: PHONE_CODE,
  password: "",
  invite_code: "",
  verify_code: "",
  cpf: "",
  user_type: 0,
  accept_promo_contract: true,
  accept_app_contract: true,
};

/**
 * 处理邀请码长度限制
 *
 * @param inviteCode 原始邀请码
 * @param maxLength 最大长度，默认20
 * @returns 处理后的邀请码
 */
const processInviteCode = (
  inviteCode: string | undefined,
  maxLength = 20,
): string => {
  if (!inviteCode) return "";
  return inviteCode.length > maxLength
    ? inviteCode.substring(0, maxLength)
    : inviteCode;
};

const defaultPersonProfile: RegisterV3ReqPersonProfile = {
  name: "",
  father_surname: "",
  mother_surname: "",
  country: "",
  province: "",
  city: "",
  community: "",
  street: "",
  house_no: "",
  postal_code: "",
  sex: 0,
  contact_a_code: PHONE_CODE,
  contact_phone: "",
  contact_email: "",
};

const defaultValidateCode: RegisterV3ReqVadidateCode = {};
// phone_code: "",
// email_code: "",
// pass_phone_code_verification: "",
// pass_email_code_verification: "",
// check_enabled_register_validate_code_sess_id: "",
// };

export const [countryOptions, setCountryOptions] = createSignal<Option[]>([]);
export const [regionOptions, setRegionOptions] = createSignal<Option[]>([]);
export const [birthError, setBirthError] = createSignal("");
export const [showInviteCode, setShowInviteCode] = createSignal(false);
export const loadCountries = async () => {
  try {
    const data = await fetchCountries();
    const countries: Option[] = data.map((country) => ({
      label: country.countryName,
      value: country.countryShortCode,
    }));
    setCountryOptions(countries);
  } catch (error) {
    console.error("加载国家数据失败:", error);
  }
};

export const loadRegionsByCountry = async (countryCode: string) => {
  try {
    const data = await fetchCountries();
    const country = data.find((c) => c.countryShortCode === countryCode);
    if (!country) {
      setRegionOptions([]);
      return;
    }
    const regions: Option[] = country.regions.map((region) => ({
      label: region.name,
      value: region.shortCode,
    }));
    setRegionOptions(regions);
  } catch (error) {
    console.error("加载省份数据失败:", error);
  }
};

// 获取数据
export const fetchCountries = async () => {
  const res = await fetch("/country.json");
  if (!res.ok) throw new Error("Failed to load country data");
  return res.json() as Promise<Country[]>;
};

export const getFormConfig = (formName: keyof RegisterRegFormConfig) => {
  const sysConfig = sysConfigStore.sysConfigData;
  const formConfig: RegisterRegFormConfig | undefined =
    sysConfig.reg_form_config as RegisterRegFormConfig;

  const config = formConfig?.[formName];

  if (formName === "base_info_step") {
    const fields = [
      {
        enabled: true,
        field: "phone",
        is_required: true,
      },
      {
        enabled: true,
        field: "email",
        is_required: true,
      },
      {
        enabled: true,
        field: "invite_code",
        is_required: false,
      },
      {
        enabled: true,
        field: "accept_app_contract",
        is_required: true,
      },
      {
        enabled: true,
        field: "accept_promo_contract",
        is_required: true,
      },
      {
        enabled: true,
        field: "cpf",
        is_required: true,
      },
      {
        enabled: true,
        field: "enabled_google_login",
        is_required: false,
      },
      {
        field: "password",
        enabled: true,
        is_required: true,
      },
    ];
    return {
      fields: fields,
    };
  }
  return config;
};

const isFieldEnabled =
  (formName: keyof RegisterRegFormConfig) => (field: string) => {
    return getFormConfig(formName)?.fields?.some(
      (item) => item.field === field && item.enabled,
    );
  };

const isFieldRequired =
  (formName: keyof RegisterRegFormConfig) => (field: string) => {
    return getFormConfig(formName)?.fields?.some(
      (item) => item.field === field && item.is_required,
    );
  };

export const isFieldEnabled_baseInfo = isFieldEnabled("base_info_step");
export const isFieldRequired_baseInfo = isFieldRequired("base_info_step");
export const isFieldEnabled_personProfile = isFieldEnabled(
  "person_profile_step",
);
export const isFieldRequired_personProfile = isFieldRequired(
  "person_profile_step",
);
export const isFieldEnabled_validateCode = isFieldEnabled("validate_code_step");
export const isFieldRequired_validateCode =
  isFieldRequired("validate_code_step");
export const [baseInfo, setBaseInfo] = createStore<RegisterBaseInfo>(
  JSON.parse(JSON.stringify(defaultBaseInfo)),
);

export function useRegisterForm() {
  const { t } = useI18n();
  const { back, push } = useRouter();
  const { showDialog, closeDialog } = useDialog();
  const [step, setStep] = createSignal(1);
  const { saveCookieParams, getStoredParams, params } = useUrlStorage();

  const isMultiAuth = createMemo(() => {
    return getWebsiteConfigV2()?.registerSwitch; // true: 开启电话/email/cpf验证, false: 只开启cpf验证
  });

  // Loading state
  const [isLoading, setIsLoading] = createSignal(false);

  // Agree to terms state
  const [agreeToTerms, setAgreeToTerms] = createSignal(false);
  // cf turnstile status
  const [isPassCfTurnstile, setIsPassCfTurnstile] = createSignal(false);

  // CF 抖动动画状态
  const [isCfShaking, setIsCfShaking] = createSignal(false);

  const [endTime, setEndTime] = createSignal<number | null>(null);

  // Input type state (email or phone)
  const [inputType, setInputType] = createSignal<"email" | "phone">("email");

  // 注册步骤
  const [registerStep, setRegisterStep] = createSignal<"form" | "verify">(
    "form",
  );

  const [enabledRegisterValidateCode, setEnabledRegisterValidateCode] =
    createSignal(false);

  // 输入检测输入框
  const detectInputType = (value: string): "email" | "phone" => {
    if (!value) return inputType(); // 如果为空，保持当前类型

    // 检测是否包含@符号（邮箱特征）
    if (value.includes("@")) {
      return "email";
    }

    // 检测是否包含邮箱常见字符
    if (/[a-zA-Z@.]/.test(value)) {
      return "email";
    }

    // 检测是否为纯数字（手机号特征）
    // 改进：支持任意长度的纯数字字符串，特别是复制粘贴场景
    if (/^\d+$/.test(value)) {
      return "phone";
    }

    // 特殊处理：如果输入包含数字且没有邮箱特征字符，优先识别为电话
    // 这能处理一些边界情况，如用户输入 "11999999999" 这样的纯数字
    if (/\d/.test(value) && !/[a-zA-Z@]/.test(value)) {
      return "phone";
    }

    // 默认保持当前类型
    return inputType();
  };

  // 智能输入处理函数
  const handleSmartInput = (value: string) => {
    const detectedType = detectInputType(value);
    let typeChanged = false;

    // 如果检测到的类型与当前类型不同，自动切换
    if (detectedType !== inputType()) {
      setInputType(detectedType);
      typeChanged = true;

      // 清空另一个字段的值
      if (detectedType === "email") {
        setBaseInfo("phone", "");
        setBaseInfoErrors("phone", "");
      } else {
        setBaseInfo("email", "");
        setBaseInfoErrors("email", "");
      }
    }

    // 更新对应字段的值
    if (detectedType === "email") {
      setBaseInfo("email", value);
    } else {
      setBaseInfo("phone", value);
    }

    return typeChanged;
  };

  // 智能输入处理函数 h5
  const handleH5SmartInput = (value: string) => {
    const detectedType = detectInputType(value);
    let typeChanged = false;

    // 如果检测到的类型与当前类型不同，自动切换
    if (detectedType !== inputType()) {
      setInputType(detectedType);
      typeChanged = true;

      // 清空另一个字段的值
      if (detectedType === "email") {
        setBaseInfoErrors("phone", "");
      } else {
        setBaseInfoErrors("email", "");
      }
    }

    // 更新对应字段的值
    if (detectedType === "email") {
      setBaseInfo("email", value);
    } else {
      setBaseInfo("phone", value);
    }

    return typeChanged;
  };

  // 创建 store 管理每个表单的数据

  const [personProfile, setPersonProfile] =
    createStore<RegisterV3ReqPersonProfile>(defaultPersonProfile);
  const [validateCode, setValidateCode] =
    createStore<RegisterV3ReqVadidateCode>(defaultValidateCode);

  // 错误状态管理
  const [baseInfoErrors, setBaseInfoErrors] = createStore<
    Partial<Record<keyof RegisterV3ReqBaseInfo, string>>
  >({});
  const [personProfileErrors, setPersonProfileErrors] = createStore<
    Partial<Record<keyof RegisterV3ReqPersonProfile, string>>
  >({});
  const [validateCodeErrors, setValidateCodeErrors] = createStore<
    Partial<Record<keyof RegisterV3ReqVadidateCode, string>>
  >({});

  // 获取 cf 配置
  const cloudflareTurnstileCfg = createMemo(() => {
    if (!sysConfigStore.sysConfigData.turnstile_public_key) return;
    return JSON.parse(sysConfigStore.sysConfigData.turnstile_public_key);
  });

  // 重置方法
  const resetBaseInfo = () => {
    setBaseInfo(defaultBaseInfo);
    setBaseInfoErrors({});
  };

  const resetPersonProfile = () => {
    setPersonProfile({ ...defaultPersonProfile });
    setPersonProfileErrors({});
  };

  const resetValidateCode = () => {
    setValidateCode({ ...defaultValidateCode });
    setValidateCodeErrors({});
  };

  onMount(() => {
    if (deviceInfo.app_type == 14) {
      getTwaData();
    } else {
      getSdkData();
    }
    saveCookieParams();
    tryGetServerHash();
    resetBaseInfo();
    resetPersonProfile();
    resetValidateCode();
    const urlParams = getStoredParams().params;
    if (urlParams?.ic) {
      setBaseInfo("invite_code", urlParams.ic);
    }
  });

  const reportEvent = (type: string, result: boolean) => {
    if (!type) return;
    switch (type) {
      case "email":
      case "phone":
        {
          const eventName = result
            ? "registration_check_success"
            : "registration_check_fail";
          sdkManager.tdReportEvent(`${eventName}`, {});
        }
        break;
      case "password":
        {
          const eventName = result
            ? "register_password_check_success"
            : "register_password_check_fail";
          sdkManager.tdReportEvent(`${eventName}`, {});
        }
        break;
      case "cpf":
        {
          const eventName = result
            ? "register_cpf_check_success"
            : "register_cpf_check_fail";
          sdkManager.tdReportEvent(`${eventName}`, {});
        }
        break;
      default:
        break;
    }
  };

  const validate_BaseInfo = (
    fieldOrData: keyof RegisterBaseInfo | RegisterBaseInfo,
    value?: string | number,
  ): boolean => {
    let isValid = true;
    const validateField = (
      field: keyof RegisterBaseInfo,
      val: string | number,
    ) => {
      if (!isFieldEnabled_baseInfo(field)) return;
      const validator = validators[field];
      const messages = errorMessagesBaseInfo(field);
      let error: string | undefined;
      if (isFieldRequired_baseInfo(field) && !val) {
        error = messages?.required ?? "";
      } else if (field === "phone") {
        error = validator(baseInfo.a_code, val as string)
          ? undefined
          : messages?.invalid;
      } else if (val && validator && !validator(val as string)) {
        error = messages?.invalid ?? "";
      }

      if (field !== "birth") setBaseInfoErrors(field, error);
      if (error) isValid = false;
    };

    if (typeof fieldOrData === "string") {
      if (value === undefined || value === null) {
        return false;
      }
      validateField(fieldOrData, value);
      reportEvent(fieldOrData, isValid);
      return isValid;
    } else {
      const data = fieldOrData;

      for (const [key, val] of Object.entries(data)) {
        const field = key as keyof RegisterBaseInfo;

        // 如果是多重认证
        if (isMultiAuth()) {
          // 如果验证是 phone
          if (field === "phone" && baseInfo.email) {
            continue;
          }
          if (field === "email" && baseInfo.phone) {
            continue;
          }
        } else {
          if (field === "phone") {
            continue;
          }
          if (field === "email") {
            continue;
          }
        }
        // if (
        //   (field === "email" && !isMultiAuth()) ||
        //   (field === "phone" && !isMultiAuth())
        // ) {
        //   //暂时屏蔽邮箱和手机号验证
        //   continue;
        // }
        validateField(field, val as string);
      }
      return isValid;
    }
  };

  const validate_PersonProfile = (
    field: keyof RegisterV3ReqPersonProfile,
    value: string | number,
  ) => {
    let error = undefined;
    const validator = validators[field as keyof typeof validators];
    const messages = errorMessagesPersonProfile;

    if (isFieldRequired_personProfile(field) && !value) {
      error = messages(field as keyof typeof messages).required;
    } else {
      if (field === "contact_phone") {
        if (validator(personProfile.contact_a_code, value as string)) {
          error = undefined;
        } else {
          error = messages(field as keyof typeof messages)?.invalid;
        }
      } else {
        if (value && validator && !validator(value as string)) {
          error = messages(field as keyof typeof messages)?.invalid ?? "";
        }
      }
    }
    setPersonProfileErrors(field, error);
  };

  //注册发送验证码
  const getCode = async () => {
    let type = "";
    if (isFieldEnabled_baseInfo("email") && isFieldEnabled_baseInfo("phone")) {
      type = inputType();
    } else if (isFieldEnabled_baseInfo("email")) {
      type = "email";
    } else if (isFieldEnabled_baseInfo("phone")) {
      type = "phone";
    }
    const result = await GetPhoneVerifyCode({
      phone: type === "phone" ? baseInfo.phone : "",
      code_type: CodeTypeEnum.CodeTypeRegister,
      acode: type === "phone" ? baseInfo.a_code : "",
      lang: getLangKey(),
      verify_type:
        type === "email"
          ? VerifyTypeEnum.VerifyTypeEmail
          : VerifyTypeEnum.VerifyTypePhone,
      email: type === "email" ? baseInfo.email : "",
      retry: 0,
      device: deviceInfo,
    });
    message.show(t("tips.code_sent"));
  };

  const validatePersonProfileForm = (
    data: RegisterV3ReqPersonProfile,
  ): boolean => {
    let isValid = true;

    Object.entries(data).forEach(([field, value]) => {
      const key = field as keyof RegisterV3ReqPersonProfile;

      if (!isFieldEnabled_personProfile(key)) return;
      const validator = validators[key as keyof typeof validators];
      const messages = errorMessagesPersonProfile(
        key as keyof typeof errorMessagesPersonProfile,
      );
      let error = "";

      if (isFieldRequired_personProfile(key) && !value) {
        error = messages?.required ?? "";
      } else if (
        value &&
        validator &&
        messages?.invalid &&
        !validator(value as string)
      ) {
        error = messages.invalid;
      }

      setPersonProfileErrors(key, error);
      if (error) isValid = false;
    });

    return isValid;
  };

  const checkEnabledRegisterValidateCode = async () => {
    try {
      setIsLoading(true);
      const res = await CheckEnabledRegisterValidateCode({
        invite_code: baseInfo.invite_code,
        device_id: deviceInfo.device_id,
      });

      setValidateCode(
        "check_enabled_register_validate_code_sess_id",
        res?.sess_id || "",
      );
      const enabled = res?.enabled;
      if (enabled) {
        setEnabledRegisterValidateCode(true);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  //检查账号是否已经存在
  const checkAccountExists = async (type) => {
    const req: CheckUserAccExistsReq = {
      account: "",
      email: "",
      phone: "",
      a_code: "",
      cpf: baseInfo.cpf,
    };

    if (type === "email") {
      req.email = await rsaEncrypt(baseInfo.email);
    } else {
      req.phone = await rsaEncrypt(baseInfo.phone);
      req.a_code = baseInfo.a_code;
    }

    const res = await CheckUserAccExists(req);
    if (res?.exists) {
      setBaseInfoErrors(type, t("tops.error_1314"));
      return true;
    } else if (res?.cpf_duplicated) {
      setBaseInfoErrors("cpf", t("tops.error_1348"));
      return true;
    }
    return false;
  };

  const checkAccountIsExists = async () => {
    const req: CheckUserAccExistsReq = {
      account: baseInfo.account,
      email: "",
      phone: "",
      a_code: "",
      cpf: baseInfo.cpf,
    };

    const res = await CheckUserAccExists(req);
    if (res.exists) {
      setBaseInfoErrors("account", t("tops.error_1314"));
      return true;
    } else if (res?.cpf_duplicated) {
      setBaseInfoErrors("cpf", t("tops.error_1348"));
      return true;
    }
    return false;
  };

  //后台提交
  const submit_server = async () => {
    const { default: SparkMD5 } = await import("@repo/sparkmd5");
    const md5Pwd = SparkMD5.hash(baseInfo.password || "");
    if (baseInfo.invite_code) {
      sdkManager.tdReportEvent("register_pg_invite_code_input", {});
    }

    const submitBaseInfo = {
      a_code: baseInfo.a_code,
      password: md5Pwd,
      invite_code: processInviteCode(baseInfo.invite_code),
      cpf: baseInfo.cpf,
      user_type: 0,
    };

    if (isMultiAuth()) {
      if (inputType() === "phone") {
        submitBaseInfo.phone = await rsaEncrypt(baseInfo.phone);
      }
      if (inputType() === "email") {
        submitBaseInfo.email = await rsaEncrypt(baseInfo.email);
      }
    }

    const submitPersonProfile = {
      name: personProfile.name,
      father_surname: personProfile.father_surname,
      mother_surname: personProfile.mother_surname,
      country: personProfile.country,
      province: personProfile.province,
      city: personProfile.city,
      community: personProfile.community,
      street: personProfile.street,
      house_no: personProfile.house_no,
      postal_code: personProfile.postal_code,
      sex: personProfile.sex,
      contact_a_code: personProfile.contact_a_code,
      contact_phone: personProfile.contact_phone,
      contact_email: personProfile.contact_email,
      nickname: undefined,
      head_img: undefined,
      entrus_bank_account: undefined,
      entrus_bank_user: undefined,
      put_type: undefined,
    };

    const fbInitData: Record<string, string> = getLocalStorageItem(
      LocalStorageKey.FacebookInit,
      {},
    );
    const twaInitData: Record<string, string> = getLocalStorageItem(
      LocalStorageKey.TwaInit,
      {},
    );
    // const rawConfigVersionHistory = getLocalStorageItem(
    //   LocalStorageKey.ConfigVersionHistory,
    //   {} as Record<string, unknown>,
    // );

    // // 重新组装成 Record<string, string> 格式，以防万一数据格式不一致
    // const configVersionHistory: Record<string, string> = {};
    // Object.entries(rawConfigVersionHistory).forEach(([key, value]) => {
    //   if (typeof value === "string") {
    //     configVersionHistory[key] = value;
    //   } else {
    //     // 如果不是字符串，则序列化为字符串
    //     configVersionHistory[key] = JSON.stringify(value);
    //   }
    // });

    const ad_type = getStoredParams()?.params?.ad_type || 0;
    const ad_id = getStoredParams()?.params?.ad_id || "0";
    const ad_extra = getStoredParams()?.params?.ad_extra || "";

    const req: RegisterV3Req = {
      base_info: submitBaseInfo,
      person_profile: submitPersonProfile,
      validate_code: validateCode,

      extra: {
        pack: configStore.configVersion?.pack_no || 0,
        device: deviceInfo,
        reg_info: undefined,
        first_map: "",
        source_map: "",
        app_sdk_data: undefined,
        is_experience_point: false,
        advertise_src: "",
        advertise_group: "",
        advertise_affiliate_id: "",
        // advertise_type: 0,
        advertise_type: Number(ad_type),
        advertise_id: ad_id,
        // advertise_extra: "",
        advertise_extra: ad_extra,
        google_play_integrity_token: "",
        visit_url:
          getStoredParams()?.url || params?.url || window.location.href,
        client_version: getCurrentVersion() || "",
        url_referers: getStoredParams()?.reffer || [],
        ad_referer: getAdReferer(),
        app_package_info: getAppPackageInfoParams(),
        client_params: convertObjectToStringRecord({
          app_type: deviceInfo.app_type.toString(),
          ...getAdParams(),
          ...fbInitData,
          ...twaInitData,
          // ...configVersionHistory,
        }),
      },
    };
    setIsLoading(true);
    try {
      // const paramLength = new TextEncoder().encode(JSON.stringify(req)).length;
      // console.error("paramLength", paramLength);
      // if (paramLength > 1000) {
      //   const gzipParam = compressData(req);
      //   // 使用可靠的 base64 编码
      //   const gzippedBuffer = encodeBase64(gzipParam);
      //   // 测试完整的压缩和解压缩流程
      //   const testResult = testCompression(req);
      //   console.error("压缩测试结果:", testResult);
      //   req = "z:" + gzippedBuffer;
      // }
      const res = await useRegisterApi(req);

      // console.error("paramLength", paramLength);
      if (res) {
        const obj = { username: baseInfo.phone, a_code: baseInfo.a_code };
        setLocalStorageItem(
          LocalStorageKey.REMEMBER_ME,
          aesEncryptWithCommonKey(JSON.stringify(obj)),
        );
        setLocalStorageItem(LocalStorageKey.LOGIN_TYPE, LoginTypeEnum.phone);
        useAfterLogin().afterLoginSuccess();
        upDateUserStore(res);
        await getUserInfo(true);
        back();
        // 重置首页弹窗标记，允许注册后显示弹窗
        DialogSettingCenter.resetHomePopupFlag();
        getHomePopups();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const checkRegisterCode = async () => {
    console.log("validateCode", validateCode);
    const res = await VerifyRegisterCode({
      verify_type:
        inputType() === "phone"
          ? VerifyTypeEnum.VerifyTypePhone
          : VerifyTypeEnum.VerifyTypeEmail,
      phone: inputType() === "phone" ? baseInfo.phone : "",
      acode: inputType() === "phone" ? baseInfo.a_code : "",
      email: inputType() === "email" ? baseInfo.email : "",
      code: baseInfo.verify_code,
      check_reg_validate_code_sess_id:
        validateCode.check_enabled_register_validate_code_sess_id,
    });

    if (res) {
      if (inputType() === "email") {
        setValidateCode("email_code", baseInfo.verify_code || "");
        setValidateCode("pass_email_code_verification", res?.pass_verification);
      } else if (inputType() === "phone") {
        setValidateCode("phone_code", baseInfo.verify_code || "");
        setValidateCode("pass_phone_code_verification", res?.pass_verification);
      }
      return true;
    }
    return false;
  };

  const getVerifyCode = async (
    type: "email" | "phone",
    isRetry: boolean = false,
  ) => {
    const params: Partial<GetPhoneVerifyCodeReq> = {
      code_type: CodeTypeEnum.CodeTypeRegister,
      retry: isRetry ? 1 : 0,
      device: {
        UA: navigator.userAgent,
        sign: "web",
        client_type: 0,
        app_type: 0,
        m_app: 0,
      },
      lang: getLangKey(),
    };

    if (type === "phone") {
      params.phone = await rsaEncrypt(baseInfo.phone);
      params.acode = baseInfo.a_code;
      params.verify_type = VerifyTypeEnum.VerifyTypePhone;
    }
    if (type === "email") {
      params.email = await rsaEncrypt(baseInfo.email);
      params.verify_type = VerifyTypeEnum.VerifyTypeEmail;
    }

    const res = await GetPhoneVerifyCode(params as GetPhoneVerifyCodeReq);
    if (res) {
      const interval =
        type === "phone"
          ? sysConfigStore.sysConfigData.sending_interval.phone_interval
          : sysConfigStore.sysConfigData.sending_interval.email_interval;

      setEndTime(Date.now() / 1000 + interval);
      setRegisterStep("verify");
      message.show(t("tips.code_sent"));
      // setPhoneRetry(1);

      if (isMobile()) {
        showDialog({
          type: DialogType.VerifyCode,
          data: {
            title:
              inputType() === "phone"
                ? t("settings.phoneverification")
                : t("settings.email_verification"),
            flag: inputType() as "phone" | "email",
            phone: inputType() === "phone" ? baseInfo.phone : "",
            email: inputType() === "email" ? baseInfo.email : "",
            aCode: inputType() === "phone" ? baseInfo.a_code : "",
            onVerifyCodeChange: async (value: string) => {
              setBaseInfo("verify_code", value);
            },
            onConfirm: async () => {
              if (isLoading()) return;

              try {
                setIsLoading(true);

                const isVerifyCodePass = await checkRegisterCode();
                if (!isVerifyCodePass) return;
                await submit_server();
                setRegisterStep("form");
                closeDialog(DialogType.VerifyCode);
              } catch (error) {
                console.error(error);
              } finally {
                setIsLoading(false);
              }
            },
            onResendCode: async () => {
              await getVerifyCode(inputType() as "phone" | "email", true);
            },
          },
        });
      }
    }
  };

  const countDown = createMemo(() => {
    const timeStamp = useTimestampInSeconds();
    const currentEndTime = endTime();
    if (!currentEndTime) return null;

    const total = Math.max(Math.floor(currentEndTime - timeStamp()), 0);
    if (!total) {
      setEndTime(0);
      return null;
    }
    return addZero(total);
  });

  //提交
  const onSubmit = async () => {
    sdkManager.tdReportEvent("register_submit", {});

    // 强制验证当前显示的字段
    const currentField = inputType();
    let hasError = false;

    if (isMultiAuth()) {
      if (currentField === "email") {
        if (!baseInfo.email) {
          setBaseInfoErrors("email", t("auth.emailrequired"));
          hasError = true;
        }
      } else if (currentField === "phone") {
        if (!baseInfo.phone) {
          setBaseInfoErrors("phone", t("auth.phonerequired"));
          hasError = true;
        }
      }
    } else if (!baseInfo.cpf) {
      setBaseInfoErrors("cpf", t("auth.cpfrequired"));
      hasError = true;
    }

    // 检查密码字段
    if (!baseInfo.password) {
      setBaseInfoErrors("password", t("auth.passwordrequired"));
      hasError = true;
    }

    if (hasError) return;

    const isValid = validate_BaseInfo(baseInfo);

    if (!isValid) return;
    // 检查CF验证状态
    if (
      // 表示沒有驗證
      window.hasCf &&
      cloudflareTurnstileCfg()?.enable === 1 && // 系統是否开启 cf
      // 手动模式：null表示还没勾选，false表示验证失败
      ((window.mode === "manual" && !window.isPassCfTurnstile) ||
        // 自动模式：false表示验证失败
        (window.mode === "auto" && window.isPassCfTurnstile === false))
    ) {
      // 触发抖动动画
      setIsCfShaking(true);
      setTimeout(() => setIsCfShaking(false), 300);
      return;
    }

    if (baseInfo.accept_app_contract) {
      const isVerifyStepEnabled =
        sysConfigStore?.sysConfigData?.reg_form_config?.validate_code_step
          ?.enabled === true;

      await checkEnabledRegisterValidateCode();

      if (isMultiAuth()) {
        if (
          isVerifyStepEnabled ||
          (!isVerifyStepEnabled && enabledRegisterValidateCode())
        ) {
          if (baseInfo.email) {
            if (await checkAccountExists("email")) return;
            getVerifyCode("email");
          } else if (baseInfo.phone) {
            if (await checkAccountExists("phone")) return;
            getVerifyCode("phone");
          }
        } else {
          await submit_server();
        }
      } else {
        await submit_server();
      }
    } else {
      showDialog({
        type: DialogType.Tips,
        data: {
          title: t("tips.tips"),
          content: t("tips.policies_content"),
          confirmText: t("tips.agree"),
          onConfirm: async () => {
            setBaseInfo("accept_app_contract", true);
            await onSubmit();
          },
          cancelText: false,
        },
      });
    }
  };

  //回退
  const onBack = () => {
    const current = step();
    const config = sysConfigStore.sysConfigData
      .reg_form_config as RegisterRegFormConfig;

    if (current === 3) {
      if (config.person_profile_step.enabled) {
        setStep(2);
      } else {
        setStep(1);
      }
    } else if (current === 2) {
      setStep(1);
    }
  };

  const closePage = () => {
    sdkManager.tdReportEvent("register_exit", {});

    back();
    setTimeout(() => {
      showDialog({
        type: DialogType.RetainPopup,
      });
    });
  };
  // 打开注册界面隐私政策
  const openPrivacyPolicy = () => {
    const privacyCfg = configStore.frontCfgMap?.["RegPrivacyCfg"]?.["privacy"];
    if (privacyCfg) {
      // click(privacyCfg.click_id, privacyCfg.click_params);
      // br跳转帮助中心
      push("/help");
    }
  };
  // 打开注册界面用户协议
  const openUserAgreement = () => {
    const termsCfg = configStore.frontCfgMap?.["RegPrivacyCfg"]?.["terms"];
    if (termsCfg) {
      // click(termsCfg.click_id, termsCfg.click_params);
      // br跳转帮助中心
      push("/help");
    } else {
      openPrivacyPolicy();
    }
  };
  return {
    // 基本信息表单数据和配置
    baseInfo,
    baseInfoErrors,
    setBaseInfoErrors,
    setBaseInfo,
    resetBaseInfo,

    // 个人资料表单数据和配置
    personProfile,
    personProfileErrors,
    setPersonProfileErrors,
    setPersonProfile,
    resetPersonProfile,

    // 验证码表单数据和配置
    validateCode,
    validateCodeErrors,
    setValidateCodeErrors,
    setValidateCode,
    resetValidateCode,

    // 步骤
    step,
    setStep,

    // 加载状态
    isLoading,
    setIsLoading,

    // 同意条款
    agreeToTerms,
    setAgreeToTerms,

    // cf turnstile status
    isPassCfTurnstile,
    setIsPassCfTurnstile,

    // CF 抖动动画状态
    isCfShaking,
    setIsCfShaking,

    // 输入类型
    inputType,
    setInputType,

    // 智能输入检测
    detectInputType,
    handleSmartInput,
    handleH5SmartInput,

    // 提交
    onSubmit,
    submit_server,

    validate_BaseInfo,
    validate_PersonProfile,
    birthError,
    setBirthError,
    onBack,
    getCode,
    closePage,
    showInviteCode,
    setShowInviteCode,
    openPrivacyPolicy,
    openUserAgreement,

    // 是否开启电话/email/cpf验证
    isMultiAuth,

    // 验证码到计时
    countDown,
    // 注册验证码步骤
    registerStep,
    setRegisterStep,
    // 获取验证码
    getVerifyCode,
    // 检查验证码
    checkRegisterCode,
    enabledRegisterValidateCode,
  };
}
